import DateFormat from '../../shared/helpers/date-format'
import storage from '../../services/storage'
import {
  clearTicketsAppSessionData,
  totalTickets,
  hasTicketWith,
  addOnMinimums,
  inManualUpdate,
  hasMemberTickets,
  ticketMatches,
  hasYouthTickets,
  notMembershipType,
  hasChicagoTickets,
  ZONE_TIME_CUTOFF
} from '../../shared/helpers/logic'
import { DateTime } from 'luxon'
import { has } from 'lodash-es'

const TEST_CURRENT_DATE = storage.getItem('testCurrentDate')

let TEST_JS_DATE = null
if (TEST_CURRENT_DATE) {
  const [year, month, day] = TEST_CURRENT_DATE.split('-')
  TEST_JS_DATE = new Date(year, month - 1, day)
}
const { fancyFull } = DateFormat

const itemIds = items => {
  return items.map(item =>
    item.externalId != null ? item.externalId : item.id
  )
}

const differentDays = (dateOne, dateTwo) => {
  if (!dateOne || !dateTwo) return false
  let dates = [dateOne, dateTwo]
  // here we convert any js dates to a chicago zoned luxon date
  // we do not care about time in this instance
  dates = dates.map(date => {
    if (date.isLuxonDateTime) return date
    return DateTime.fromObject({
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      zone: 'America/Chicago'
    })
  })
  return dates[0].day !== dates[1].day
}

/*
This function attempt to transfer tickets from the "Base Event" (ie: Shedd Admission or an EE) to the "Add On" event (ex: 4D)
*/
export const transferTickets = (transferTickets, receiverTickets) => {
  // Grab all of the tickets for the base event that have a quantity > 0 (ie: were added to the cart)
  let transferTicketsWithQuantity = transferTickets.filter(
    ticket => ticket.quantity > 0
  )

  // This holds the pricing for the Add On event.  The quantities will be updated based on the quantities in the Base Event and their corresponding mapping.
  let newReceiverTickets = receiverTickets

  // Initially try and map the Base Event tickets to the add on tickets using ageGroup, member status (true/false), AND pricing group
  let excessTickets = transferTicketsWithQuantity.filter(tTicket => {
    const receiverTicket = receiverTickets.find((rTicket, index) => {
      // look for matching priceTypeIds first
      if (rTicket.id === tTicket.id) {
        newReceiverTickets[index].quantity += tTicket.quantity
        return true
      }

      // pull out the ageGroup, member status, and pricing group associated with the Base Event Ticket (ex: Shedd Admission or BTS)
      let { ageGroup, member, pricingGroup } = tTicket

      /*
      If the ageGroup for the ticket from the base event that we are looking at is "youth" but the tickets we are trying match against do NOT
      have any youth pricing, default the age group to adult (this was pre-existing logic, I just reversed the order of the ifs so it didn't search
      for youth tickets if we weren't even concerned with that age group
      */
      if (ageGroup.includes('youth') && !hasYouthTickets(receiverTickets)) {
        ageGroup = member ? 'child' : 'adult'
      }

      // Get the pricing group from the base event (existing logic)
      pricingGroup =
        pricingGroup === 'default' && rTicket.pricingGroup === 'addon'
          ? 'addon'
          : pricingGroup

      // if there is only one price type for the addon, make everything match so all base event tickets get an addon (minus infants)
      if (receiverTickets.length === 1 && ageGroup !== 'infant') {
        pricingGroup = rTicket.pricingGroup
        member = !rTicket.member ? false : member
        // pass in whatever ageGroup the addon has available
        ageGroup = rTicket.ageGroup
      }

      /*
      Call the ticketMatches function with the ticket from the Base Event that we're looking at, and the age group, member, and pricing group
      from the Add On ticket and see if there is a match
      * */
      if (ticketMatches(rTicket, ageGroup, member, pricingGroup)) {
        // If we find a match, then update the corresponding item in the newReceiverTickets array at the index we're looking at (existing logic).
        newReceiverTickets[index].quantity += tTicket.quantity
        return true
      } else {
        return false
      }
    })
    return !receiverTicket
  })

  /*
  Check and see if there are any excess tickets.  If there are, then that means there were some tickets that could NOT map between events based
  on ageGroup, member and pricing group (ex: Chicago Tickets in the Base Event that don't exist in the 4D).
  The logic below will try and map on just ageGroup and member.
  */
  if (excessTickets.length !== 0) {
    // Go through the array of "excessTickets" and filter them again based on ageGroup and member only.
    let existingExtraTickets = excessTickets.filter(tTicket => {
      // Only match based on ageGroup and member type (NOT pricing group)
      let { ageGroup, member, pricingGroup } = tTicket
      // If the tickets we're trying to match do NOT contain youth tickets but the age group of the ticket we're looking for is youth, then
      // set it to adult (existing logic).
      if (ageGroup.includes('youth') && !hasYouthTickets(receiverTickets)) {
        ageGroup = member ? 'child' : 'adult'
      }

      // Go through the most recent update of tickets we have and look for matches between the excess tickets and this one
      const foundMatch = newReceiverTickets.find(rTicket => {
        // Call the ticketMatches function again with the ticket from the Base Event, the age group and the member status
        if (ticketMatches(rTicket, ageGroup, member, pricingGroup, true)) {
          // If we find a match, update the quantity of the base event ticket with the quantity from the matching add on event.
          rTicket.quantity = rTicket.quantity + tTicket.quantity
          return true
        } else {
          return false
        }
      })
      // Return the negation of this so that if a match was found, it IS filtered from the array.
      return !foundMatch
    })

    // When we get to here there should be no extra tickets...if there are, then something is not going to be right in the cart.
    if (existingExtraTickets.length > 0) {
      const error = {
        message:
          'There was a problem transferring the following tickets between events',
        untransferredTickets: existingExtraTickets
      }
      console.warn(error)
    }
  }

  return newReceiverTickets
}

export const inCart = (event, cart) => {
  let cartItem = null
  if (event.times && event.times.length !== 0) {
    const eventTimeIds = itemIds(event.times)
    cartItem = cart.items.find(
      item =>
        eventTimeIds.includes(item.id) || eventTimeIds.includes(item.externalId)
    )
  } else {
    cartItem = cart.items.find(item => {
      const itemId = item.externalId != null ? item.externalId : item.id
      return itemId === event.id
    })
  }

  return cartItem
}

export const groupByType = typeGroups => {
  const typeSort = ['nonMember', 'member'].filter(type =>
    Object.prototype.hasOwnProperty.call(typeGroups, type)
  )
  return typeSort.map(key => [key, typeGroups[key]])
}

export const sortByAge = priceGroups => {
  const newConstituencies = []

  Object.entries(priceGroups).forEach(([memberKey, ageGroups]) => {
    // using safe navigator because some priceGroups don't use default
    if (
      ageGroups?.['types']?.['default']?.['hasConstituencyPricing'] === true
    ) {
      newConstituencies.push(memberKey)
    }
  })

  const ageSort = [
    'employee',
    'adult',
    'adult2',
    'youth',
    'youth2',
    'child',
    'child2',
    'infant',
    'generic1',
    'generic2',
    'generic3',
    'generic4',
    'generic5',
    'generic6',
    'generic7',
    'generic8',
    'generic9',
    'generic10'
  ]
  let concatArray = newConstituencies.concat(ageSort)
  let filteredArray = concatArray.filter(age =>
    Object.prototype.hasOwnProperty.call(priceGroups, age)
  )
  return filteredArray.map(key => priceGroups[key])
}

export const getNonResidentText = zoneTimeInstance => {
  let text = 'Non-Member: '
  if (
    Object.prototype.hasOwnProperty.call(
      zoneTimeInstance.pricing.nonMember.adult.types,
      'chicago'
    )
  ) {
    text = 'Non-Chicago resident: '
  } else if (
    Object.prototype.hasOwnProperty.call(
      zoneTimeInstance.pricing.nonMember.adult.types,
      'illinois'
    )
  ) {
    text = 'Non-Illinois resident: '
  }
  let child =
    zoneTimeInstance.pricing.nonMember.child !== undefined
      ? zoneTimeInstance.pricing.nonMember.child
      : zoneTimeInstance.pricing.nonMember.youth
  let childText =
    child !== undefined
      ? `, $${child.types.default.price.toFixed(2)} Children`
      : ``
  let adultText =
    zoneTimeInstance.pricing.nonMember.adult !== undefined
      ? `$${zoneTimeInstance.pricing.nonMember.adult.types.default.price.toFixed(
          2
        )} Adult`
      : ``
  text = adultText === `` ? `` : text + adultText + childText
  return text
}

export const noTickets = tickets =>
  tickets.every(ticket => ticket.quantity === 0)

export const includesConstituencyTickets = tickets =>
  hasTicketWith('hasConstituencyPricing', true, tickets, true)

export const includesAdult = tickets =>
  hasTicketWith('ageGroup', 'adult', tickets, true, 'includes')

export const includesGeneric = tickets =>
  hasTicketWith('ageGroup', 'generic', tickets, true, 'includes')

export const includesEmployee = tickets =>
  hasTicketWith('ageGroup', 'employee', tickets, true, 'includes')

export const includesChicago = tickets =>
  hasTicketWith('pricingGroup', 'chicago', tickets, false)

export const includesIllinois = tickets =>
  hasTicketWith('pricingGroup', 'illinois', tickets, false)

export const dateFromQuery = queryDate => {
  const [year, month, day] = queryDate
    .split('-')
    .map(dateElement => Number(dateElement))
  return new DateTime.fromObject({
    year,
    month,
    day,
    zone: 'America/Chicago'
  })
}

export const setFromEventSessionData = (
  fromEventId,
  step,
  privateEvent = false,
  date = null
) => {
  clearTicketsAppSessionData()
  storage.setItem('fromEventId', fromEventId)
  storage.setItem('step', step)
  storage.setItem('privateEvent', privateEvent)
  if (date) {
    storage.setItem('day', date)
  }
}

export const shouldClearSession = fromEvent =>
  !fromEvent &&
  storage.hasItem('fromEventId') &&
  !storage.hasItem('testCurrentDate')

export const queryEventOrDateChanged = (
  queryDate,
  selectedDate,
  fromEvent,
  fromEventId
) => {
  const queryEventChanged = fromEvent && fromEventId !== fromEvent
  const queryDateChanged = differentDays(queryDate, selectedDate)
  return queryEventChanged || queryDateChanged
}

export const hasAvailabilityForDependencies = (
  dependencyTickets,
  dependentAvailability
) => {
  if (dependentAvailability < totalTickets(dependencyTickets)) {
    return false
  }
  return true
}

export const isToday = date => {
  const eventDate = new Date(date)
  const today = new Date()
  return (
    eventDate.getDate() === today.getDate() &&
    eventDate.getMonth() === today.getMonth() &&
    eventDate.getFullYear() === today.getFullYear()
  )
}

export const filterCurrentDayZonesWithinCutOff = performanceZones => {
  let today = new Date()
  let todayChicago = DateTime.fromJSDate(today, { zone: 'America/Chicago' })
  let timeHour = todayChicago.hour
  let timeMin = todayChicago.minute

  let sortedZones = performanceZones.filter(zone => {
    let zTimeChicago = DateTime.fromISO(zone.zoneTime, {
      zone: 'America/Chicago'
    })
    if (zTimeChicago.hour === timeHour) {
      return timeMin <= ZONE_TIME_CUTOFF
    } else {
      return zTimeChicago.hour > timeHour
    }
  })

  return sortedZones
}

export const hasAvailability = (minimumAvailability, event) => {
  if (event.privateEvent == true && !storage.hasItem('fromEventId')) {
    return false
  } else if (event.times.length > 0) {
    return event.times.some(time => time.availability >= minimumAvailability)
  } else {
    if (event.performances[0].zones.length > 1) {
      return event.performances[0].zones.some(
        zone => zone.availableCount >= minimumAvailability && zone.zoneTime
      )
    } else {
      return event.performances[0].zones.some(
        zone => zone.availableCount >= minimumAvailability
      )
    }
  }
}

export const queryEventNotChosenEvent = (fromEventId, chosenEventId) =>
  fromEventId && chosenEventId !== fromEventId

export const optInParty = (eventToOptIn, optInEvent) => {
  const { tickets } = optInEvent
  // event to opt in is the main event (ie: Shedd Admission or EE, tickets are the add on
  const optedInTickets = transferTickets(eventToOptIn.tickets, tickets)
  return { ...optInEvent, tickets: optedInTickets }
}

export const getMinimums = (event, items) => {
  const addOnItems = items.filter(
    item =>
      item.type.includes('addon') &&
      fancyFull(item.occursAt) === fancyFull(event.occursAt)
  )

  const inManualUpdateAddOnsUpdated =
    inManualUpdate &&
    addOnItems.every(
      addOnItem =>
        hasMemberTickets(addOnItem.tickets) || !has(addOnItem.pricing, 'member')
    )

  return event && inManualUpdateAddOnsUpdated
    ? addOnMinimums(addOnItems, event)
    : null
}

export const onSameDay = (eventOne, eventTwo) => {
  let times = [eventOne, eventTwo].map(event => {
    let time = event.occursAt
    if (!time) {
      if (event.times.length == 0) {
        const errorMessage = `No times coming back for ${event.name}
        , please check Tessitura`
        const error = new Error(errorMessage)
        error.code = 'console'
        console.warn(errorMessage)
        throw error
      }
      time = event.times[0].occursAt
    }
    return DateTime.fromISO(time, { zone: 'America/Chicago' })
  })

  const sameDay = times[0].day === times[1].day
  return sameDay
}

export const hitManualUpdateMax = (event, manualMaximum) => {
  return totalTickets(event.tickets) >= manualMaximum
}

export const findAddOnsFor = (event, items) => {
  return items.filter(
    item =>
      !['standalone', 'general', 'membership_plan'].includes(item.type) &&
      onSameDay(event, item)
  )
}

export const cartWithoutEvent = (event, items) => {
  const eventItems = items.filter(notMembershipType)
  return eventItems.filter(
    item => onSameDay(event, item) && event.id !== item.externalId
  )
}

export const isExpired = expiresAt => {
  let now = Date.now()
  let expiresAtDate = null

  if (TEST_JS_DATE) {
    now = TEST_JS_DATE
    expiresAtDate = new Date(now)
    expiresAtDate = expiresAtDate.setDate(expiresAtDate.getDate() + 1)
  }

  const expiresAtDateTime = expiresAtDate
    ? DateTime.fromMillis(expiresAtDate)
    : DateTime.fromISO(expiresAt)

  const nowDateTime = TEST_JS_DATE
    ? DateTime.fromJSDate(TEST_JS_DATE)
    : DateTime.fromMillis(Date.now())
  const dateTimeNow = nowDateTime.setZone('America/Chicago')

  const dateTimeFromNow = expiresAtDateTime.setZone('America/Chicago')
  const difference = dateTimeFromNow.diff(dateTimeNow)

  return difference <= 0
}

export const getPricingObject = (event, id) => {
  let pricing = event.pricing
  if (!pricing) {
    const time = event.times.find(event => event.id === id)
    if (!time) {
      console.warn(`Cannot find time with id ${id} on ${event.name} event`)
      pricing = null
    } else {
      pricing = time.pricing
    }
  }
  // this can fail silently so that the app does not crash but we get warnings and can see visually which prices are missing
  // we do not have control over the tessitura issues that cause these prices to be null, having this fail in this way
  // allows us to debug and find these issues quickly with visual cues and console warnings.
  if (!pricing) {
    console.warn(`Cannot find pricing object for ${event.name} event`)
  }
  return pricing
}

export const hasOnlyChicagoTickets = tickets => {
  return hasChicagoTickets(tickets, true)
}

export {
  hitGeneralAdmissionMax,
  hitEmployeeMax,
  hitMembershipMax,
  clearTicketsAppSessionData,
  totalTickets,
  hasPriceTypes,
  anyMembershipTickets,
  isMembershipType,
  hasMemberTickets,
  inManualUpdate,
  isStandalone,
  isChildLearning,
  ticketMatches,
  addOnMinimums,
  notMembershipType,
  hasIllinoisTickets,
  hasChicagoTickets,
  totalMemberTickets
} from '../../shared/helpers/logic'
